import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, Maximize2, MessageSquarePlus, Sparkles, X } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import useCopilot from "../../hooks/useCopilot";
import apiClient from "../../lib/apiClient";
import CopilotComposer from "./CopilotComposer";
import CopilotSuggestions from "./CopilotSuggestions";
import CopilotThread from "./CopilotThread";
import { COPILOT_QUICK_PROMPTS } from "./constants";

const COPILOT_ROUTE = "/ai/copilot";

/**
 * App-wide copilot entry point: a floating pill that opens a docked chat panel.
 *
 * Mounted once in AppLayout, so it is available on every authenticated screen.
 * It hides itself on the full-page workspace to avoid two chats side by side.
 * The panel and the page share a session id via sessionStorage (see useCopilot),
 * so "expand" carries the conversation across.
 */
const CopilotLauncher = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const {
        sessionId,
        messages,
        sending,
        loadingHistory,
        feedbackState,
        send,
        rate,
        openSession,
        startNewChat,
    } = useCopilot();

    const employeeName = sessionStorage.getItem("employee_name") || "";
    const firstName = employeeName.split(" ")[0] || "";
    const isHr =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    // Close the panel on navigation so it never covers a screen the user just opened.
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Esc closes the panel, matching the rest of the app's overlays.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open]);

    // Fetch proactive smart nudges on open
    const [nudges, setNudges] = useState([]);
    useEffect(() => {
        if (!open) return;
        const fetchNudges = async () => {
            try {
                const resData = await apiClient.get("ai/nudges/");
                if (resData?.status === "success" && Array.isArray(resData.data)) {
                    setNudges(resData.data);
                }
            } catch (e) {
                console.error("Failed to fetch smart nudges:", e);
            }
        };
        fetchNudges();
    }, [open]);

    // Restore the thread the first time the panel is opened in this page view.
    // The session id survives navigation via sessionStorage, so the backend
    // already treats these as one conversation and feeds prior turns to the
    // model — showing an empty panel would contradict that. Silent, because a
    // session that has never been sent yet has no history to fetch.
    const restoredRef = useRef(false);
    useEffect(() => {
        if (!open || restoredRef.current || messages.length) return;
        restoredRef.current = true;
        openSession(sessionId, { silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleNewChat = () => {
        restoredRef.current = true;
        startNewChat();
    };

    // Draggable position state for the launcher button with localStorage persistence
    const STORAGE_KEY = "copilot_launcher_position";
    const [position, setPosition] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    const isDraggingRef = useRef(false);
    const lastPosRef = useRef(position);
    const dragStartRef = useRef({ startX: 0, startY: 0, buttonX: 0, buttonY: 0 });

    const savePosition = (pos) => {
        if (pos) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
            } catch (e) {
                console.error("Failed to save launcher position:", e);
            }
        }
    };

    const handleMouseDown = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        dragStartRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            buttonX: rect.left,
            buttonY: rect.top,
        };
        isDraggingRef.current = false;

        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - dragStartRef.current.startX;
            const deltaY = moveEvent.clientY - dragStartRef.current.startY;
            if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
                isDraggingRef.current = true;
            }

            const newX = Math.max(10, Math.min(window.innerWidth - rect.width - 10, dragStartRef.current.buttonX + deltaX));
            const newY = Math.max(10, Math.min(window.innerHeight - rect.height - 10, dragStartRef.current.buttonY + deltaY));
            const newPos = { x: newX, y: newY };
            lastPosRef.current = newPos;
            setPosition(newPos);
        };

        const handleMouseUp = () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            if (isDraggingRef.current && lastPosRef.current) {
                savePosition(lastPosRef.current);
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = (e) => {
        if (!e.touches[0]) return;
        const touch = e.touches[0];
        const rect = e.currentTarget.getBoundingClientRect();
        dragStartRef.current = {
            startX: touch.clientX,
            startY: touch.clientY,
            buttonX: rect.left,
            buttonY: rect.top,
        };
        isDraggingRef.current = false;

        const handleTouchMove = (moveEvent) => {
            if (!moveEvent.touches[0]) return;
            const t = moveEvent.touches[0];
            const deltaX = t.clientX - dragStartRef.current.startX;
            const deltaY = t.clientY - dragStartRef.current.startY;
            if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
                isDraggingRef.current = true;
            }

            const newX = Math.max(10, Math.min(window.innerWidth - rect.width - 10, dragStartRef.current.buttonX + deltaX));
            const newY = Math.max(10, Math.min(window.innerHeight - rect.height - 10, dragStartRef.current.buttonY + deltaY));
            const newPos = { x: newX, y: newY };
            lastPosRef.current = newPos;
            setPosition(newPos);
        };

        const handleTouchEnd = () => {
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            if (isDraggingRef.current && lastPosRef.current) {
                savePosition(lastPosRef.current);
            }
        };

        window.addEventListener("touchmove", handleTouchMove);
        window.addEventListener("touchend", handleTouchEnd);
    };

    const handleClick = (e) => {
        if (isDraggingRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        setOpen(true);
    };

    // The workspace page is the copilot — no launcher needed there.
    if (location.pathname.startsWith(COPILOT_ROUTE)) return null;

    if (!open) {
        const buttonStyle = position
            ? { left: `${position.x}px`, top: `${position.y}px`, bottom: "auto", right: "auto" }
            : {};

        return (
            <button
                type="button"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onClick={handleClick}
                style={buttonStyle}
                title="Ask the AI Copilot (Drag to reposition)"
                className={cn(
                    "fixed z-30 inline-flex items-center gap-2 rounded-full cursor-grab active:cursor-grabbing select-none",
                    !position && "bottom-5 right-5",
                    "bg-sidebar px-4 py-2.5 text-sm font-semibold text-white",
                    "shadow-lg transition-all hover:bg-sidebar-primary hover:shadow-xl",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar focus-visible:ring-offset-2 animate-bounce"
                )}
            >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Ask AI</span>
            </button>
        );
    }

    return (
        <div
            className={cn(
                "fixed z-30 flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl",
                // Full-width sheet on mobile, docked card from sm up.
                "inset-x-3 bottom-3 top-16 sm:inset-x-auto sm:top-auto",
                "sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[400px]"
            )}
            role="dialog"
            aria-label="AI Copilot"
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 bg-sidebar px-3.5 py-3 text-white">
                <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
                        <Bot className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">AI Copilot</p>
                        <p className="flex items-center gap-1.5 text-[11px] text-white/80">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                            Connected to your HR data
                        </p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        onClick={handleNewChat}
                        title="New chat"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <MessageSquarePlus className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            navigate(COPILOT_ROUTE);
                        }}
                        title="Open full workspace"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <Maximize2 className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        title="Close"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Quick prompts — hidden once a conversation is under way, to give
                the thread the full height. */}
            {!messages.length && (
                <CopilotSuggestions
                    items={nudges.length > 0 ? nudges : COPILOT_QUICK_PROMPTS}
                    onPick={send}
                    disabled={sending}
                    isHr={isHr}
                    compact
                />
            )}

            <CopilotThread
                messages={messages}
                sending={sending}
                loading={loadingHistory}
                userName={firstName}
                feedbackState={feedbackState}
                onRate={rate}
                compact
            />

            <div className="border-t border-border bg-background p-2.5">
                <CopilotComposer
                    onSend={send}
                    disabled={sending}
                    placeholder="Ask the copilot…"
                    compact
                    autoFocus
                />
            </div>
        </div>
    );
};

export default CopilotLauncher;
